import { IngredientsContext } from "@/app/clientLayout";
import {
  searchManyRecipeLinks,
  searchRecipeLink,
  supportedRecipeSites,
} from "@/lib/scrape-utilities";
import useIndexedDB from "@/lib/useIndexedDB";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import {
  ChevronUpDownIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import LinkImage from "@/components/LinkImage";
import BouncingLoader from "@/components/styles/BouncingLoader";
import UndrawEmptyStreet from "@/components/svg/UndrawEmptyStreet";
import cuid from "cuid";
import { combineUnits, searchForUnit } from "@/lib/measurement";
import { RecipeSearchPayload, ScrapedRecipeData } from "@/lib/types";
import useDebounce from "@/lib/useDebounce";
import { ellipsise, searchInventory } from "@/lib/util";
import Link from "next/link";
import {
  SearchIngredientsDocument,
  SearchIngredientsQuery,
  SearchIngredientsQueryVariables,
} from "@/queries/autogenerate/graphql";
import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import { compareTwoStrings } from "string-similarity";
import { useClient } from "urql";
import { CurrentUser } from "../Page";
import DishIcon from "../svg/DishIcon";
import FinalisePartialRecipe, { PartialRecipe } from "./FinalisePartialRecipe";

const searchSiteOptions = supportedRecipeSites;

interface Props {
  user: CurrentUser;
  setQueryFn?: (query: string) => void;
}

const SearchRecipeWebsites: React.FC<Props> = ({ user, setQueryFn }) => {
  const client = useClient();

  const { ingredients } = useContext(IngredientsContext);

  const [query, setQuery] = useIndexedDB(
    "recipeSearchQuery",
    "",
    !user.saveRecipeProgress
  );

  const [searchSite, setSearchSite] = useState(searchSiteOptions[0]);
  const [searching, setSearching] = useState(false);

  const [searchPayload, setSearchPayload] =
    useState<RecipeSearchPayload | null>(null);
  const [selectedURL, setSelectedURL] = useState<string | null>(null);

  const [recipeData, setRecipeData] = useIndexedDB<PartialRecipe[] | null>(
    "searchRecipeData3",
    null,
    !user.saveRecipeProgress
  );

  const queryInput = useRef<HTMLInputElement>(null);

  const reset = useMemo(
    () => () => {
      setQuery("");
      if (setQueryFn) {
        setQueryFn("");
      }
      setSearching(false);
      setSearchPayload(null);
      setSelectedURL(null);

      if (queryInput.current != document.activeElement) {
        queryInput.current?.focus();
      }
    },
    []
  );

  const debouncedQuery = useDebounce(query, 350);

  // Here's where the API call happens
  // We use useEffect since this is an asynchronous action
  useEffect(
    () => {
      // Set up an abort controller, to abort on exit
      const abortController = new AbortController();

      // Make sure we have a value (user has entered something in input)
      if (debouncedQuery) {
        // Set isSearchingSingle state
        setSearching(true);

        console.log("Let's goooo 👉👉👉💪🏾", debouncedQuery);

        // Fire off our API call
        try {
          searchManyRecipeLinks(
            searchSite.searchURL + encodeURIComponent(debouncedQuery),
            abortController
          )
            .then(async (ogData) => {
              let data = ogData;
              console.log("DATA FROM MIATCH", data);

              if (data.error) {
                console.log("retrying...");

                toast("The search was not successful. Trying one more time.", {
                  position: "top-center",
                  duration: 10 * 1000,
                });
                console.error(data.error);

                data = await searchManyRecipeLinks(
                  searchSite.searchURL + encodeURIComponent(debouncedQuery),
                  abortController
                );
              }

              if (data.error) {
                setSearching(false);

                toast.error(data.error);
                console.error(data.error);

                return;
              }
              if (!data.data) {
                setSearching(false);

                toast.error("There was an error.");
                console.error("There was an error.");

                return;
              }

              const { recipes } = data.data;

              console.log("recipes", recipes);

              setSearchPayload(data.data);
              //   setSearchedIngredients(
              //     ingredients.map((i) => ({
              //       ...i,
              //       unit: searchForUnit(units, i?.unit, ingredient.density)?.id,
              //     }))
              //   );

              setSearching(false);
            })
            .catch((error) => {
              // Ignore AbortErrors
              if (error?.name !== "AbortError") {
                toast.error(`There was an error.`);

                reset();
                console.error(error);
                console.error("Something went wrong");
              }
            });
        } catch (error) {
          reset();
          console.error(error);
          console.error("Something went wrong");
        }
      } else {
        setSearching(false);
        setSearchPayload(null);
      }

      return function cancel() {
        abortController.abort();
      };
    },
    // This is the useEffect input array
    // Our useEffect function will only execute if this value changes ...
    // ... and thanks to our hook it will only change if the original ...
    // value (searchTerm) hasn't changed for more than 500ms.
    [debouncedQuery, searchSite.searchURL]
  );

  // Here's where the API call happens
  // We use useEffect since this is an asynchronous action
  useEffect(
    () => {
      // Set up an abort controller, to abort on exit
      const abortController = new AbortController();

      // Make sure we have a value (user has entered something in input)
      if (selectedURL) {
        // Set isSearchingSingle state
        setSearching(true);

        console.log("Let's goooo 👉👉👉💪🏾", selectedURL);

        // Fire off our API call
        try {
          // setSearching(true);
          searchRecipeLink(selectedURL, abortController)
            .then(async (result: ScrapedRecipeData) => {
              console.log("DATA FROM MIATCH", result);

              const { error, data } = result;

              if (error) {
                setSearching(false);
                setSelectedURL(null);

                toast.error(error);
                console.error(error);

                return;
              }
              if (!data) {
                setSearching(false);
                setSelectedURL(null);

                toast.error("There was an error.");
                console.error("There was an error.");

                return;
              }

              const tempIngredients: PartialRecipe["ingredientSections"][number]["ingredients"] =
                await Promise.all(
                  data.ingredients.map(
                    async ({ text, amount, unit: unitText, name }) => {
                      const searchedIngredients = searchInventory(
                        name || text,
                        ingredients
                      ).slice(0, 5);

                      const matchingIngredients =
                        searchedIngredients
                          .map((i) => ({
                            ...i,
                            id: i.id,
                            similar: true,
                            similarity: Math.round(
                              compareTwoStrings(
                                i.name.toLowerCase(),
                                (name || text).toLowerCase()
                              ) * 100
                            ),
                          }))
                          .sort((a, b) => b.similarity - a.similarity) ?? [];

                      const [highestMatch] = matchingIngredients;
                      const highestValidMatch =
                        highestMatch && highestMatch.similarity >= 60
                          ? highestMatch
                          : null;

                      return {
                        uuid: cuid(),
                        text: text,
                        amount: Number(amount) || 1,
                        unitId: highestValidMatch
                          ? searchForUnit(
                              combineUnits(highestValidMatch),
                              unitText,
                              highestValidMatch.density ?? undefined,
                              highestValidMatch.selectionUnit ?? undefined
                            )?.id
                          : undefined,
                        measurement: {
                          label: "Discrete Units",
                          value: null,
                        },
                        derivedUnit: unitText, // unit derived from the data
                        isChecked: false, // for density
                        density: 845,
                        tags: [],
                        selected: highestValidMatch || undefined,
                        options: [...matchingIngredients],
                      };
                    }
                  )
                );

              setRecipeData([
                {
                  uuid: cuid(),
                  name: data.name,
                  public: false,
                  source: data.source,
                  process: "searched website",
                  image: data.image,
                  servings: parseFloat(data.servings) || 1,
                  method: data.method,
                  tags: [],
                  ingredientSections: [
                    {
                      uuid: cuid(),
                      name: "Default Section",
                      section: false,
                      ingredients: tempIngredients,
                    },
                  ],
                },
              ]);

              setSearching(false);
            })
            .catch((error) => {
              if (error?.name !== "AbortError") {
                console.log(typeof error);

                reset();
                console.error(error);

                toast.error("There was an error");
              }
            });
        } catch (error) {
          setSearching(false);
          console.error(error);

          toast.error("There was an error");
        }
      } else {
        setSearching(false);
      }

      return function cancel() {
        abortController.abort();
      };
    },
    // This is the useEffect input array
    // Our useEffect function will only execute if this value changes ...
    // ... and thanks to our hook it will only change if the original ...
    // value (searchTerm) hasn't changed for more than 500ms.
    [selectedURL]
  );

  return !recipeData ? (
    <div className="m-3 w-full text-center">
      <div className="my-5">
        <label htmlFor="name">
          <h2 className="mb-5 flex items-center justify-center gap-2">
            <MagnifyingGlassIcon className="w-5 align-middle text-amber-400" />
            Search Recipe Websites
            <span className="tag tag-cyan">Beta</span>
          </h2>
        </label>
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
          className="group relative mx-auto mb-3 flex w-full max-w-4xl items-center rounded-md border border-solid border-gray-300 bg-white px-0.5 py-2 shadow-lg focus-within:outline focus-within:outline-blue-700"
        >
          <Listbox
            value={searchSite}
            onChange={(newsite) => {
              setSearchSite(newsite);
            }}
          >
            <ListboxButton className="mx-1.5 my-0 flex h-full shrink-0 items-center gap-1.5 rounded bg-gray-200 p-1.5 transition hover:bg-gray-300 focus:bg-gray-300">
              <LinkImage link={searchSite.searchURL || undefined} no_link />
              <span className="font-normal">{searchSite.title}</span>
              <ChevronUpDownIcon className="w-5 shrink-0" />
            </ListboxButton>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <ListboxOptions className="absolute top-12 z-10 max-h-96 space-y-2.5 overflow-auto rounded-md bg-white py-0.5 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {searchSiteOptions.map((site) => (
                  <ListboxOption
                    key={site.baseURL}
                    value={site}
                    //   disabled={person.unavailable}
                    className="mx-1.5 flex cursor-pointer items-center gap-1.5 text-center"
                  >
                    <div className="flex w-9 items-center justify-center">
                      <LinkImage link={site.searchURL || undefined} no_link />
                    </div>
                    <span className="font-normal">{site.title}</span>
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Transition>
          </Listbox>
          <input
            type="text"
            name="name"
            placeholder="cake..."
            className="w-full flex-grow p-1.5 text-2xl outline-none"
            ref={queryInput}
            autoFocus
            onChange={(e) => {
              setQuery(e.target.value);
              if (setQueryFn) {
                setQueryFn(e.target.value);
              }
            }}
            value={query}
            required
          />
          {query ? (
            <button
              className="absolute right-1.5"
              onClick={(e) => {
                setQuery("");
                if (setQueryFn) {
                  setQueryFn("");
                }
              }}
            >
              <XCircleIcon className="icon-lg text-gray-400 group-hover:text-gray-600" />
            </button>
          ) : null}
        </form>
        <div className="mx-auto mb-3 max-w-4xl text-left">
          <a
            href={searchSite.URL}
            target="_blank"
            rel="noreferrer"
            className="font-normal text-blue-500 hover:text-blue-700"
          >
            {searchSite.URL.replace(/^(https?:\/\/)?(www\.?)?/, "")}
          </a>
        </div>

        {searching ? <div className="spinner mt-8 h-11 w-11"></div> : null}

        {selectedURL ? (
          <div className="mt-14">
            <p className="text-xl font-normal text-gray-500">
              Fetching recipe from{" "}
              <a
                href={selectedURL}
                target="_blank"
                rel="noreferrer"
                className="text-blue-500 hover:text-blue-700"
              >
                {selectedURL.replace(/^(https?:\/\/)?(www\.?)?/, "")}
              </a>
              .{" "}
              <button
                className="btn btn-subtle ml-2.5 text-lg"
                onClick={() => {
                  setSelectedURL(null);
                }}
              >
                Cancel
              </button>
            </p>
          </div>
        ) : searchPayload ? (
          searchPayload.recipes.length ? (
            <div className="mx-auto w-max">
              <div className="mb-10 mt-14 flex items-center justify-between px-1.5">
                <div className="text-left font-normal text-gray-500">
                  <p className="text-2xl">
                    {searchPayload.recipes.length} results
                  </p>
                  <small>
                    From{" "}
                    <a
                      href={searchPayload.source}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-500 hover:text-blue-700"
                    >
                      {searchPayload.source.replace(
                        /^(https?:\/\/)?(www\.?)?/,
                        ""
                      )}
                    </a>
                  </small>
                </div>
              </div>

              <div className="mx-auto grid w-max grid-cols-4 gap-5 xl:grid-cols-3 lg:grid-cols-2 md:grid-cols-1">
                {searchPayload.recipes.map((r) => (
                  <div
                    key={r.source}
                    className="group relative w-64 rounded-lg bg-white p-3 text-left shadow-md transition duration-150 ease-in-out hover:shadow-lg"
                  >
                    <button
                      className="btn btn-primary absolute right-3 top-3 font-normal opacity-0 transition group-hover:opacity-100"
                      onClick={() => {
                        setSelectedURL(r.source);
                      }}
                    >
                      Select
                    </button>

                    <div
                      className="h-52 w-full rounded-lg bg-gradient-to-tr from-cyan-100 to-green-100 bg-cover shadow-lg"
                      style={
                        r.image ? { backgroundImage: `url(${r.image})` } : {}
                      }
                    />

                    <h3
                      title={r.name}
                      className="my-3 line-clamp-2 font-semibold"
                    >
                      {r.name}
                    </h3>
                    <p>
                      <a href={r.source} target="_blank" rel="noreferrer">
                        <LinkIcon className="w-5 text-gray-600" />{" "}
                        {ellipsise(
                          r.source.replace(/^(https?:\/\/)?(www\.?)?/, ""),
                          25
                        )}
                      </a>
                    </p>
                    {/* <p>
                      <AcademicCap className="w-5 text-gray-600" />{" "}
                      {school.name}
                    </p> */}
                    {r.servings ? (
                      <p>
                        <DishIcon className="w-5 text-gray-600" /> {r.servings}{" "}
                        Servings
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-18">
              <UndrawEmptyStreet className="w-88" />
              <div className="mt-10 text-center">
                <h1 className="mb-2">No Results Found</h1>
              </div>
            </div>
          )
        ) : null}
      </div>
    </div>
  ) : (
    <>
      <button
        className="btn"
        onClick={() => {
          setSearching(false);
          setSelectedURL(null);
          setRecipeData(null);

          setTimeout(() => {
            if (queryInput.current != document.activeElement) {
              queryInput.current?.focus();
            }
          }, 500);
        }}
      >
        Search Again
      </button>
      {searching ? (
        <BouncingLoader size={15} />
      ) : (
        <FinalisePartialRecipe
          initialRecipes={recipeData}
          updateRecipes={setRecipeData}
          onCompleted={(recipeIDs) => {
            // toast, with a link to the recipe, if only one recipe was created
            toast.success((t) => (
              <div>
                {recipeIDs.length > 1 ? (
                  <p>
                    {recipeIDs.length} recipes have been created. You can view
                    and edit them from the recipes page.
                  </p>
                ) : (
                  <p>
                    <span className="font-normal">{recipeData[0].name}</span>{" "}
                    has been created. You can view and edit it from the recipes
                    page.
                  </p>
                )}
                {recipeIDs.length === 1 ? (
                  <Link
                    prefetch={false}
                    href={`/recipes?folder=${recipeIDs[0].folderID || ""}#${
                      recipeIDs[0].id
                    }`}
                    className="btn btn-sm btn-subtle"
                    onClick={() => {
                      toast.dismiss(t.id);
                    }}
                  >
                    View Recipe
                  </Link>
                ) : null}
              </div>
            ));

            reset();

            // if (onCompleted) {
            //   onCompleted();
            // }
          }}
        />
      )}
    </>
  );
};

export default SearchRecipeWebsites;
